import { useMemo } from 'react';

import { QueryDecoder } from '@monoid-dev/zod-endec';
import { InvalidSearch } from '@utils/errors';
import { omitBy, isUndefined } from 'lodash-es';
import { stringify, parse } from 'query-string';
import { useHistory, useLocation } from 'react-router-dom';
import { ZodType, ZodTypeDef } from 'zod';

export const useSearch = <T, D extends ZodTypeDef, O>(type: ZodType<T, D, O>) => {
  const history = useHistory();
  const location = useLocation();

  const decoder = useMemo(() => {
    return new QueryDecoder(type);
  }, [type]);

  const search = useMemo(() => {
    const e = decoder.safeDecode(location.search);
    if (e.success) {
      return type.parse(e.data);
    } else {
      throw new InvalidSearch(`Invalid search: ${JSON.stringify(e.input)}`);
    }
  }, [decoder, location.search]);

  const sanitize = (p: any) => omitBy(p, (value) => isUndefined(value));

  return {
    search,
    updateSearch: (p: Partial<T>) => {
      // To keep the origianl search, we need to spread the current one because extra props are stripped.
      const sanitized = sanitize({ ...parse(location.search), ...search, ...p });
      history.push(`${location.pathname}?${stringify(sanitized)}`);
    },
    setSearch: (p: T) => {
      const sanitized = sanitize(p);
      history.push(`${location.pathname}?${stringify(sanitized)}`);
    },
    clearSearch: () => {
      history.push(location.pathname);
    },
  };
};
